import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetGroupAccessQuery } from '../../../api/group/groupAdminApiSlice';

interface Props {
    groupId: string;
    roomName: string;
    /** The organization whose dialog this is — not named in its own warning. */
    organizationId: string;
    /** After saving, is the room open to everyone ("Öffentlich")? */
    willBeOpen: boolean;
}

/**
 * Open or closed is ONE fact per room (ADR-0008 amendment 2026-10-13), so a
 * choice in one organization's dialog opens or closes the room for every
 * organization assigned to it, and for externals. Said before saving, naming
 * who else is affected. Reads the room's current assignments itself, so the
 * dialog only renders one of these per room whose state may change.
 */
const RoomOpennessWarning: React.FC<Props> = ({ groupId, roomName, organizationId, willBeOpen }) => {
    const { t } = useTranslation();
    const { data, isError, isLoading } = useGetGroupAccessQuery({ groupId });

    // A failure must never look like "nobody else is affected" (#1579).
    if (isError) {
        return <p className={'text-sm text-foreground'}>{t('organization.rooms.assign.openness_load_error', { room: roomName })}</p>;
    }
    // Said while it loads, so a quick Save is not mistaken for "nobody else
    // is affected".
    if (isLoading || !data) {
        return <p className={'text-sm text-foreground/60'}>{t('organization.rooms.assign.openness_checking', { room: roomName })}</p>;
    }

    const others = (data.organizations ?? [])
        .filter((organization) => organization.id !== organizationId)
        .map((organization) => organization.name);
    const assigned = data.organizationIds.length > 0;
    // A deployment that does not send `restricted` treats any grant as closing.
    const isOpenNow = !assigned || data.restricted === false;
    const alsoFor = others.length > 0
        ? ` ${t('organization.rooms.assign.also_for', { organizations: others.join(', ') })}`
        : '';

    if (willBeOpen && !isOpenNow && others.length > 0) {
        return <p className={'text-sm text-foreground'}>{t('organization.rooms.assign.opens_room', { room: roomName })}{alsoFor}</p>;
    }
    // Closing a room that an organization is covered on while it stays public:
    // externals lose it. A room nobody was assigned to is simply being given to
    // this organization, which is what assigning has always meant.
    if (!willBeOpen && isOpenNow && assigned) {
        return <p className={'text-sm text-foreground'}>{t('organization.rooms.assign.closes_room', { room: roomName })}{alsoFor}</p>;
    }
    return null;
};

export default RoomOpennessWarning;
